"use client"

// Standalone decompose Sheet — opens when the user clicks a `Solo` cell in
// the matrix. Lists every standalone line_item for that row identity in
// the period, with per-line fragment margin (Q7 resolution).
//
// Lazy data load via useStandaloneDecompose(enabled=open). Server-side
// pagination (50 / page, max 200).

import { useEffect, useMemo, useState } from "react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { ArrowDown, ArrowUp, ArrowUpDown, AlertTriangle, Eye, EyeOff, Info } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useStandaloneDecompose } from "./use-standalone-decompose"
import { computeEconomicsRow } from "./economics-calculations"
import { TableUpdatingBar, TableUpdatingPill } from "./table-updating-indicator"
import { OrdersLink } from "@/components/shopify/cross-sell/orders-link"
import type { EconomicsSettings } from "./types"
import type { StandaloneSortKey } from "@/lib/shopify/bundles/standalone-line-items"
import type {
  MatrixGroupBy,
  MatrixRowIdentity,
  StandaloneLineItemRow,
} from "@/lib/shopify/bundles/types"

type SortState = { key: StandaloneSortKey; dir: "asc" | "desc" } | null

function nextSort(prev: SortState, key: StandaloneSortKey): SortState {
  if (!prev || prev.key !== key) return { key, dir: "desc" }
  if (prev.dir === "desc") return { key, dir: "asc" }
  return null
}

const fmtCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
const fmtInt = new Intl.NumberFormat("en-US")
const PAGE_SIZE = 50

const FRAGMENT_MARGIN_TOOLTIP =
  "Per-line margin: shipping / Shopify fee / CAC are allocated as if this single line_item were the entire order. " +
  "Useful for ranking lines by within-period contribution — NOT for tenant-level P&L roll-up."

function formatTimestamp(iso: string | null, timezone: string): string {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return iso
  }
}

interface StandaloneDecomposeSheetProps {
  integrationId: string
  identity: MatrixRowIdentity | null
  groupBy: MatrixGroupBy
  monthStartUtc: string
  monthEndUtc: string
  /** User's IANA timezone — anchors processed_at formatting to the analytics tz. */
  timezone: string
  minimumOrderTotal: number
  economicsSettings: EconomicsSettings
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function StandaloneDecomposeSheet({
  integrationId,
  identity,
  groupBy,
  monthStartUtc,
  monthEndUtc,
  timezone,
  minimumOrderTotal,
  economicsSettings,
  open,
  onOpenChange,
}: StandaloneDecomposeSheetProps) {
  const [offset, setOffset] = useState(0)
  // Family-mode "eye" excludes: sku → variant_title (for the pinned restore row).
  const [excludedSkus, setExcludedSkus] = useState<Map<string, string | null>>(new Map())
  const [sort, setSort] = useState<SortState>(null)

  const excludeList = useMemo(() => Array.from(excludedSkus.keys()), [excludedSkus])
  // Stable string value (Object.is on primitives) so the debounce effect only
  // re-arms when the SET CONTENT changes, not on every render.
  const excludeKey = useMemo(() => JSON.stringify(excludeList), [excludeList])

  // Debounce eye-toggle bursts (~200ms) before the hook refetches — each change
  // re-runs the full-period in-memory standalone pipeline on the server, so a
  // rapid burst of excludes collapses into ONE refetch. The pinned placeholders
  // + the client-side row hide (below) give instant visual feedback meanwhile.
  const [debouncedExcludeKey, setDebouncedExcludeKey] = useState("[]")
  useEffect(() => {
    const t = setTimeout(() => setDebouncedExcludeKey(excludeKey), 200)
    return () => clearTimeout(t)
  }, [excludeKey])
  const excludeForHook = useMemo<string[]>(() => JSON.parse(debouncedExcludeKey), [debouncedExcludeKey])

  // Opening a different row (or re-opening) → full reset.
  useEffect(() => {
    if (!open) return
    setOffset(0)
    setExcludedSkus(new Map())
    setSort(null)
    setDebouncedExcludeKey("[]")
  }, [identity?.key, open])

  // Authoritative exclude/sort change → back to page 1 (offset owned here, not
  // the hook). Keyed on the DEBOUNCED exclude so the reset aligns with the
  // refetch that actually fires.
  useEffect(() => {
    setOffset(0)
  }, [debouncedExcludeKey, sort])

  const handleSort = (key: StandaloneSortKey) => setSort((prev) => nextSort(prev, key))

  const handleExclude = (sku: string, variantTitle: string | null) => {
    setExcludedSkus((prev) => {
      const next = new Map(prev)
      next.set(sku, variantTitle)
      return next
    })
  }
  const handleRestore = (sku: string) => {
    setExcludedSkus((prev) => {
      const next = new Map(prev)
      next.delete(sku)
      return next
    })
  }

  const { data, isLoading, error } = useStandaloneDecompose({
    integrationId,
    periodStart: monthStartUtc,
    periodEnd: monthEndUtc,
    groupBy,
    sku: identity?.sku ?? null,
    familyKey: identity?.family_key ?? null,
    minimumOrderTotal,
    freeShippingThreshold: economicsSettings.free_shipping_threshold,
    limit: PAGE_SIZE,
    offset,
    excludeSkus: excludeForHook,
    sortBy: sort?.key ?? null,
    sortDir: sort?.dir ?? "desc",
    enabled: open,
  })

  const hasExcluded = excludedSkus.size > 0
  // Pending = the user toggled an exclude but the debounced refetch hasn't fired
  // yet. Treat as "updating" so the indicator shows during the debounce window.
  const excludePending = debouncedExcludeKey !== excludeKey
  const isUpdating = (isLoading && data != null) || excludePending
  const canExclude = groupBy === "family"

  // Instant client-side hide of just-excluded SKUs (the authoritative server
  // drop arrives after the debounce). Avoids a 200ms flash of soon-to-be-hidden
  // rows alongside their pinned placeholder.
  const visibleDataRows = useMemo(
    () => (data == null ? [] : data.rows.filter((r) => !excludedSkus.has(r.sku ?? ""))),
    [data, excludedSkus],
  )

  const total = data?.total ?? 0
  const showingFrom = total === 0 ? 0 : offset + 1
  const showingTo = data ? offset + data.rows.length : 0
  const hasPrev = offset > 0
  const hasNext = data != null && offset + data.rows.length < data.total

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="break-words">
            {identity?.label ?? "Standalone line items"}
          </SheetTitle>
          <SheetDescription className="break-words text-xs">
            <span>Standalone — line items NOT consumed by any bundle in the selected period.</span>
            {identity?.secondary_label != null && (
              <>
                <br />
                <span className="font-mono">{identity.secondary_label}</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        <TooltipProvider delayDuration={300}>
          <div className="mt-4">
            {error && (
              <Alert variant="destructive" className="mb-3">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isLoading && data == null ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : data == null ? null : visibleDataRows.length === 0 && !hasExcluded ? (
              <p className="text-sm text-muted-foreground italic">
                No standalone line items for this row in the selected period.
              </p>
            ) : (
              <>
                <div className="rounded-md border overflow-x-auto relative">
                  <TableUpdatingBar active={isUpdating} />
                  <table className={cn("w-full text-sm transition-opacity", isUpdating && "opacity-80")}>
                    <thead>
                      <tr className="border-b bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                        <SortTh label="Order" sortKey="order" sort={sort} onSort={handleSort} align="left" />
                        <SortTh label="Processed" sortKey="processed" sort={sort} onSort={handleSort} align="left" />
                        {canExclude && (
                          <th className="text-left px-3 py-2 font-medium">SKU</th>
                        )}
                        <SortTh label="Qty" sortKey="qty" sort={sort} onSort={handleSort} align="right" />
                        <SortTh label="Revenue" sortKey="revenue" sort={sort} onSort={handleSort} align="right" />
                        <SortTh label="Refund" sortKey="refund" sort={sort} onSort={handleSort} align="right" />
                        <th className="text-right px-3 py-2 font-medium">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                                Margin (fragment) <Info className="inline h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[320px] text-xs">
                              {FRAGMENT_MARGIN_TOOLTIP}
                            </TooltipContent>
                          </Tooltip>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Excluded SKUs — pinned to the top with a closed eye to restore. */}
                      {canExclude &&
                        Array.from(excludedSkus.entries()).map(([sku, variantTitle]) => (
                          <tr key={`excluded-${sku}`} className="border-b bg-muted/30">
                            <td colSpan={7} className="px-3 py-1.5">
                              <button
                                type="button"
                                onClick={() => handleRestore(sku)}
                                className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                                aria-label={`Show ${sku} again`}
                              >
                                <EyeOff className="h-3.5 w-3.5" />
                                <span className="font-mono">{sku}</span>
                                {variantTitle && <span>· {variantTitle}</span>}
                                <span className="text-muted-foreground/70">· hidden, click to show</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      {visibleDataRows.map((row) => (
                        <Row
                          key={`${row.shopify_order_id}-${row.line_item_id}`}
                          row={row}
                          integrationId={integrationId}
                          monthStartUtc={monthStartUtc}
                          monthEndUtc={monthEndUtc}
                          timezone={timezone}
                          economicsSettings={economicsSettings}
                          showSku={canExclude}
                          onExclude={canExclude ? handleExclude : undefined}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between gap-2 mt-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-2">
                    Showing {fmtInt.format(showingFrom)}–{fmtInt.format(showingTo)} of{" "}
                    {fmtInt.format(total)}
                    <TableUpdatingPill active={isUpdating} />
                  </span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!hasPrev || isLoading}
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    >
                      Prev
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!hasNext || isLoading}
                      onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </TooltipProvider>
      </SheetContent>
    </Sheet>
  )
}

function Row({
  row,
  integrationId,
  monthStartUtc,
  monthEndUtc,
  timezone,
  economicsSettings,
  showSku,
  onExclude,
}: {
  row: StandaloneLineItemRow
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  timezone: string
  economicsSettings: EconomicsSettings
  showSku: boolean
  /** Family mode — hide all rows with this SKU. */
  onExclude?: (sku: string, variantTitle: string | null) => void
}) {
  // Per-line «fragment margin» — same `computeEconomicsRow` used by the
  // matrix cells, just on a 1-row aggregator output. Caveats live in the
  // header tooltip, not per-cell.
  const computed = computeEconomicsRow(row.fragment_metrics, economicsSettings)
  const margin = computed.net_profit
  const marginColor = margin >= 0 ? "text-green-700" : "text-red-600"

  return (
    <tr className="border-b hover:bg-muted/30">
      <td className="px-3 py-2 align-top">
        <OrdersLink
          integrationId={integrationId}
          monthStartUtc={monthStartUtc}
          monthEndUtc={monthEndUtc}
          orderIds={[row.shopify_order_id]}
        >
          {row.order_name ?? row.shopify_order_id}
        </OrdersLink>
      </td>
      <td className="px-3 py-2 align-top text-muted-foreground whitespace-nowrap">
        {formatTimestamp(row.processed_at, timezone)}
      </td>
      {showSku && (
        <td className="px-3 py-2 align-top">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono text-xs break-all">{row.sku ?? "—"}</div>
              {row.variant_title && (
                <div className="text-xs text-muted-foreground">{row.variant_title}</div>
              )}
            </div>
            {onExclude && row.sku && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onExclude(row.sku!, row.variant_title)}
                    aria-label={`Hide all rows with SKU ${row.sku}`}
                    className="shrink-0 mt-0.5 text-muted-foreground/60 hover:text-foreground focus:outline-none"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Hide all rows with this SKU</TooltipContent>
              </Tooltip>
            )}
          </div>
        </td>
      )}
      <td className="text-right px-3 py-2 align-top tabular-nums">
        {fmtInt.format(row.quantity)}
      </td>
      <td className="text-right px-3 py-2 align-top tabular-nums">
        {fmtCurrency.format(row.line_total)}
      </td>
      <td className="text-right px-3 py-2 align-top tabular-nums text-muted-foreground">
        {row.refund_amount > 0 ? fmtCurrency.format(row.refund_amount) : "—"}
      </td>
      <td
        className={cn(
          "text-right px-3 py-2 align-top tabular-nums",
          marginColor,
          computed.effective_cogs == null && "text-muted-foreground/70",
        )}
      >
        {fmtCurrency.format(margin)}
      </td>
    </tr>
  )
}

function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  align,
}: {
  label: string
  sortKey: StandaloneSortKey
  sort: SortState
  onSort: (key: StandaloneSortKey) => void
  align: "left" | "right"
}) {
  const active = sort?.key === sortKey
  const Icon = !active ? ArrowUpDown : sort?.dir === "asc" ? ArrowUp : ArrowDown
  return (
    <th
      scope="col"
      aria-sort={active ? (sort?.dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn("px-3 py-2 font-medium", align === "left" ? "text-left" : "text-right")}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 select-none hover:text-foreground focus:outline-none",
          align === "right" && "justify-end w-full",
        )}
      >
        {label}
        <Icon
          className={cn("h-3 w-3 shrink-0", active ? "opacity-100" : "opacity-40")}
          aria-hidden
        />
      </button>
    </th>
  )
}
